import { forwardRef } from 'react';
import { cn } from './utils/cn';

const sizes = {
  xs: 'w-6 h-6',
  sm: 'w-10 h-10',
  md: 'w-12 h-12',
  lg: 'w-16 h-16',
  xl: 'w-24 h-24',
  preview: 'w-[100px] h-[100px]',
  fill: 'w-full h-full',
};

const placeholderSizes = {
  xs: 'text-[12px]',
  sm: 'text-[16px]',
  md: 'text-[24px]',
  lg: 'text-[32px]',
  xl: 'text-[48px]',
  preview: 'text-[48px]',
  fill: 'text-[18px]',
};

export interface ThumbnailProps {
  src?: string | null;
  alt: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'preview' | 'fill';
  selected?: boolean;
  onClick?: () => void;
  title?: string;
  className?: string;
}

export const Thumbnail = forwardRef<HTMLDivElement, ThumbnailProps>(
  ({ src, alt, size = 'md', selected, onClick, title, className }, ref) => {
    const sizeClass = sizes[size];
    const isClickable = !!onClick;

    return (
      <div ref={ref} className={cn('relative shrink-0', size === 'fill' && 'w-full h-full', className)}>
        {src ? (
          <img
            src={src}
            alt={alt}
            title={title}
            onClick={onClick}
            className={cn(
              sizeClass,
              'object-cover rounded shadow-md border transition-all',
              selected
                ? 'border-accent-primary/50'
                : 'border-border-subtle group-hover:border-border-default',
              isClickable && 'cursor-pointer hover:border-accent-primary/50'
            )}
          />
        ) : (
          <div
            className={cn(
              sizeClass,
              'bg-bg-deep border border-border-default rounded flex items-center justify-center opacity-40',
              placeholderSizes[size]
            )}
          >
            <span role="img" aria-label="No image">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="opacity-50"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
            </span>
          </div>
        )}
      </div>
    );
  }
);

Thumbnail.displayName = 'Thumbnail';
